import { useEffect, useState } from 'react';
import { subscriptionsApi } from '../api/subscriptions';

const CACHE_TTL_MS = 60000;

let cachedHasSubscription: boolean | null = null;
let cachedAt = 0;

/**
 * Any authenticated user with no subscription row (GET /subscriptions/current ->
 * subscription: null) has never started a trial or plan. Gates access to the app
 * until they pick a plan on /start-trial. Grandfathered/paid/trialing users always
 * have a subscription row and are unaffected.
 */
export function useSubscriptionGate(enabled: boolean) {
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(cachedHasSubscription);
  const [loading, setLoading] = useState(enabled && cachedHasSubscription === null);

  useEffect(() => {
    if (!enabled) return;

    const isFresh = Date.now() - cachedAt < CACHE_TTL_MS;
    if (cachedHasSubscription !== null && isFresh) {
      setHasSubscription(cachedHasSubscription);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    subscriptionsApi
      .getCurrentSubscription()
      .then((response) => {
        if (cancelled) return;
        const has = response.subscription !== null;
        cachedHasSubscription = has;
        cachedAt = Date.now();
        setHasSubscription(has);
      })
      .catch((error) => {
        console.error('Failed to check subscription gate:', error);
        // Fail open: a transient error here shouldn't lock authenticated users out of the app.
        if (!cancelled) setHasSubscription(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { hasSubscription, loading };
}

export function invalidateSubscriptionGateCache() {
  cachedHasSubscription = null;
  cachedAt = 0;
}
